import React from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@heroicons/react/24/outline";
import DefaultAvatar from "../../components/DefaultAvatar";
import HoverActionsMenu from "../../components/HoverActionsMenu";

interface QuickTaskRowProps {
  log: any;
  title: string;
  completed: boolean;
  dueLabel?: string;
  assigneeName?: string;
  assigneeAvatar?: string;
  onOpen: (id: string | number) => void;
  onToggleComplete?: (id: string | number) => void;
  onDelete?: (id: string | number) => void;
}

const QuickTaskRow: React.FC<QuickTaskRowProps> = ({
  log,
  title,
  completed,
  dueLabel,
  assigneeName,
  assigneeAvatar,
  onOpen,
  onToggleComplete,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative group flex items-start w-full">
      <div className="flex items-center w-full gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 -mx-1">
        <div className="min-w-0 flex-1 flex items-center gap-2">
          {/* Check Icon - 16x16, border gray-200, rounded 4px */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleComplete?.(log.get("id"));
            }}
            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
              completed ? "bg-blue-600 border-blue-600" : "bg-white border-gray-200"
            }`}
            aria-pressed={completed}
          >
            {completed && <CheckIcon className="w-3.5 h-3.5 text-white" />}
          </button>
          {/* Title - Inter Medium 12px, gray-900 */}
          <span
            className="text-[12px] font-medium text-gray-900 leading-4 truncate cursor-pointer"
            onClick={() => onOpen(log.get("id"))}
          >
            {title}
          </span>
        </div>

        {/* Content section: Avatar + Name + Action + Detail */}
        <div className="flex items-center gap-2 flex-2">
          {/* Avatar - 20x20 */}
          {assigneeAvatar ? (
            <img
              src={assigneeAvatar}
              alt=""
              className="w-5 h-5 rounded-full shrink-0"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : assigneeName ? (
            <DefaultAvatar
              name={assigneeName}
              size="sm"
              className="w-5 h-5 rounded-full shrink-0"
            />
          ) : null}
          {/* Name - Inter Medium 12px, gray-900 */}
          {assigneeName && (
            <span className="text-[12px] font-medium text-gray-900 leading-4 truncate">
              {assigneeName}
            </span>
          )}
          {/* Action - Inter Regular 12px, gray-500 */}
          {dueLabel && (
            <>
              <span className="text-[12px] text-gray-500 leading-4 truncate">
                {t("scheduled a deadline on")}
              </span>
              {/* Detail - Inter Medium 12px, blue-600 */}
              <span className="text-[12px] font-medium text-blue-600 leading-4 truncate shrink-0">
                {dueLabel}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Hover Actions Menu */}
      <HoverActionsMenu
        onOpen={() => onOpen(log.get("id"))}
        onDelete={onDelete ? () => onDelete(log.get("id")) : undefined}
      />
    </div>
  );
};

export default QuickTaskRow;
